<script setup lang="ts">
import { computed, useSlots } from 'vue'

// File name stays single-word; the component's real name lives here.
defineOptions({ name: 'MsButton', inheritAttrs: true })

// Two axes, not one list.
type Variant = 'outline' | 'solid' | 'soft' | 'ghost' | 'muted' | 'link'
type Tone = 'base' | 'ai' | 'danger' | 'success'

// The counter is a badge on a button, so its tones are a subset of MsBadge's.
type CounterTone = 'base' | 'accent' | 'success' | 'danger'

/** The old single-axis names, still accepted. */
type LegacyVariant = 'ai' | 'ai-ghost' | 'destructive' | 'destructive-solid'

const LEGACY: Record<LegacyVariant, { variant: Variant; tone: Tone }> = {
  ai: { variant: 'outline', tone: 'ai' },
  'ai-ghost': { variant: 'ghost', tone: 'ai' },
  destructive: { variant: 'outline', tone: 'danger' },
  'destructive-solid': { variant: 'solid', tone: 'danger' },
}

/** Which presences each tone has actually been drawn for. */
const SUPPORTED: Record<Tone, readonly Variant[]> = {
  base: ['outline', 'solid', 'soft', 'ghost', 'muted', 'link'],
  ai: ['outline', 'solid', 'soft', 'ghost'],
  danger: ['outline', 'solid', 'soft', 'ghost'],
  success: ['outline', 'solid', 'soft', 'ghost'],
}

type Size = 'xs' | 'sm' | 'default' | 'lg' | 'icon-xs' | 'icon-sm' | 'icon' | 'icon-lg'

const props = withDefaults(
  defineProps<{
    variant?: Variant | LegacyVariant
    tone?: Tone
    size?: Size
    as?: 'button' | 'a' | 'router-link' | 'label'
    block?: boolean
    pill?: boolean
    animated?: boolean
    loading?: boolean
    disabled?: boolean
    counter?: number | string | null
    counterTone?: CounterTone | 'neutral'
    type?: 'button' | 'submit' | 'reset'
    // A quiet line above the label, inside the button.
    caption?: string
  }>(),
  {
    variant: 'outline',
    tone: 'base',
    size: 'default',
    as: 'button',
    block: false,
    pill: false,
    animated: false,
    loading: false,
    disabled: false,
    counter: null,
    counterTone: 'base',
    type: 'button',
    caption: undefined,
  },
)

const slots = useSlots()
const hasCaption = computed(() => Boolean(props.caption || slots.caption))

const isInert = computed(() => props.disabled || props.loading)

const resolvedCounterTone = computed<CounterTone>(() =>
  props.counterTone === 'neutral' ? 'base' : props.counterTone,
)

const resolved = computed<{ variant: Variant; tone: Tone }>(() => {
  const legacy = LEGACY[props.variant as LegacyVariant]
  if (legacy) return legacy
  return { variant: props.variant as Variant, tone: props.tone }
})

const activeTone = computed<Tone>(() => {
  const { variant, tone: wanted } = resolved.value
  if (SUPPORTED[wanted].includes(variant)) return wanted
  if (import.meta.env.DEV) {
    console.warn(
      `[MsButton] tone="${wanted}" has no ${variant} variant — falling back to base. ` +
        `Drawn pairs: ${SUPPORTED[wanted].join(', ')}.`,
    )
  }
  return 'base'
})

const classes = computed(() => [
  'ms-button',
  `ms-button--${resolved.value.variant}`,
  `ms-button--tone-${activeTone.value}`,
  `ms-button--size-${props.size}`,
  {
    'ms-button--block': props.block,
    'ms-button--pill': props.pill,
    'ms-button--animated': props.animated,
    'ms-button--loading': props.loading,
    'ms-button--inert': props.as !== 'button' && isInert.value,
    'ms-button--captioned': hasCaption.value,
  },
])
</script>

<template>
  <component
    :is="as"
    :class="classes"
    :type="as === 'button' ? type : undefined"
    :disabled="as === 'button' ? isInert : undefined"
    :aria-disabled="as !== 'button' && isInert ? 'true' : undefined"
    :aria-busy="loading || undefined"
  >
    <span v-if="loading" class="ms-button__spinner" aria-hidden="true" />
    <span v-if="hasCaption" class="ms-button__caption">
      <slot name="caption">{{ caption }}</slot>
    </span>
    <span class="ms-button__stack">
      <span v-if="hasCaption" class="ms-button__line"><slot /></span>
      <slot v-else />
    </span>
    <span v-if="$slots.trailing" class="ms-button__trailing">
      <slot name="trailing" />
    </span>
    <span
      v-if="counter !== null && counter !== ''"
      class="ms-button__counter"
      :class="`ms-button__counter--${resolvedCounterTone}`"
    >{{ counter }}</span>
  </component>
</template>

<style scoped lang="scss">
.ms-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--btn-gap);
  border: 1px solid transparent;
  border-radius: var(--btn-radius);
  background-color: transparent;
  color: var(--fg-primary);
  font-family: inherit;
  letter-spacing: var(--tracking-normal);
  font-size: var(--btn-font-size);
  font-weight: var(--btn-font-weight);
  line-height: var(--leading-none);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color var(--btn-transition-duration) var(--btn-transition-ease),
    border-color var(--btn-transition-duration) var(--btn-transition-ease),
    color var(--btn-transition-duration) var(--btn-transition-ease);

  &:focus-visible {
    outline: none;
    box-shadow: var(--btn-focus-ring);
  }

  /* :disabled only exists on <button>; --inert covers the anchor case. */
  &:disabled,
  &--inert {
    opacity: var(--btn-disabled-opacity);
    cursor: not-allowed;
    pointer-events: none;
  }

  /* Slot content belongs to the parent scope, hence :deep. */
  :deep(svg),
  :deep(img) {
    flex-shrink: 0;
    inline-size: var(--btn-icon-size);
    block-size: var(--btn-icon-size);
  }

  /* ---- Sizes ---- */
  &--size-xs {
    height: var(--btn-height-xs);
    padding: 0 var(--sp-8);
    font-size: var(--btn-font-size-xs);
  }

  &--size-sm {
    height: var(--btn-height-sm);
    padding: 0 var(--sp-8);
  }

  &--size-default {
    height: var(--btn-height);
    padding: 0 var(--sp-8);
  }

  &--size-lg {
    height: var(--btn-height-lg);
    padding: 0 var(--sp-8);
  }

  /* Icon-only: square, no padding — replaces legacy .btn--icon / .btn--close. */
  &--size-icon-xs,
  &--size-icon-sm,
  &--size-icon,
  &--size-icon-lg {
    padding: 0;
  }

  &--size-icon-xs {
    width: var(--btn-height-xs);
    height: var(--btn-height-xs);
  }

  &--size-icon-sm {
    width: var(--btn-height-sm);
    height: var(--btn-height-sm);
  }

  &--size-icon {
    width: var(--btn-height);
    height: var(--btn-height);
  }

  &--size-icon-lg {
    width: var(--btn-height-lg);
    height: var(--btn-height-lg);
  }

  &--block {
    width: 100%;
  }

  &--pill {
    border-radius: var(--radius-pill);
  }

  /* ---- Variants ---- */
  &--solid {
    --ms-button-seam: var(--btn-solid-divider);
    --btn-counter-base-bg: var(--btn-counter-base-bg-on-fill);

    background-color: var(--btn-solid-bg);
    color: var(--btn-solid-fg);

    &:hover:not(:disabled) {
      background-color: var(--btn-solid-bg-hover);
    }
  }

  &--soft {
    --ms-button-seam: var(--btn-soft-divider);

    background-color: var(--btn-soft-bg);
    color: var(--btn-soft-fg);

    &:hover:not(:disabled) {
      background-color: var(--btn-soft-bg-hover);
      box-shadow: var(--ring-state);
    }
  }

  &--outline {
    --ms-button-seam: var(--btn-outline-divider);

    background-color: var(--btn-outline-bg);
    border-color: var(--btn-outline-border);
    color: var(--btn-outline-fg);

    &:hover:not(:disabled) {
      background-color: var(--btn-outline-bg-hover);
      border-color: var(--btn-outline-border-hover);
    }
  }

  &--ghost {
    --ms-button-seam: var(--btn-ghost-divider);

    color: var(--btn-ghost-fg);

    &:hover:not(:disabled) {
      background-color: var(--btn-ghost-bg-hover);
      box-shadow: var(--ring-state);
    }
  }

  &--muted {
    --ms-button-seam: var(--btn-muted-divider);

    color: var(--btn-muted-fg);

    &:hover:not(:disabled) {
      color: var(--btn-muted-fg-hover);
      background-color: var(--btn-muted-bg-hover);
      box-shadow: var(--ring-state);
    }
  }

  /* ---- Tones ---- A tone draws nothing of its own. */
  &--tone-danger {
    --btn-outline-bg: var(--btn-danger-outline-bg);
    --btn-outline-bg-hover: var(--btn-danger-outline-bg-hover);
    --btn-outline-border: var(--btn-danger-outline-border);
    --btn-outline-border-hover: var(--btn-danger-outline-border);
    --btn-outline-fg: var(--btn-danger-outline-fg);
    --btn-outline-divider: var(--btn-danger-outline-border);

    --btn-solid-bg: var(--btn-danger-solid-bg);
    --btn-solid-bg-hover: var(--btn-danger-solid-bg-hover);
    --btn-solid-fg: var(--btn-danger-solid-fg);
    --btn-solid-divider: var(--btn-danger-solid-divider);

    --btn-soft-bg: var(--btn-danger-soft-bg);
    --btn-soft-bg-hover: var(--btn-danger-soft-bg-hover);
    --btn-soft-fg: var(--btn-danger-soft-fg);
    --btn-soft-divider: var(--btn-danger-outline-border);

    --btn-ghost-fg: var(--btn-danger-ghost-fg);
    --btn-ghost-bg-hover: var(--btn-danger-ghost-bg-hover);
    --btn-ghost-divider: var(--btn-danger-outline-border);
  }

  &--tone-success {
    --btn-outline-bg: var(--btn-success-outline-bg);
    --btn-outline-bg-hover: var(--btn-success-outline-bg-hover);
    --btn-outline-border: var(--btn-success-outline-border);
    --btn-outline-border-hover: var(--btn-success-outline-border);
    --btn-outline-fg: var(--btn-success-outline-fg);
    --btn-outline-divider: var(--btn-success-outline-border);

    --btn-solid-bg: var(--btn-success-solid-bg);
    --btn-solid-bg-hover: var(--btn-success-solid-bg-hover);
    --btn-solid-fg: var(--btn-success-solid-fg);
    --btn-solid-divider: var(--btn-success-solid-divider);

    --btn-soft-bg: var(--btn-success-soft-bg);
    --btn-soft-bg-hover: var(--btn-success-soft-bg-hover);
    --btn-soft-fg: var(--btn-success-soft-fg);
    --btn-soft-divider: var(--btn-success-outline-border);

    --btn-ghost-fg: var(--btn-success-ghost-fg);
    --btn-ghost-bg-hover: var(--btn-success-ghost-bg-hover);
    --btn-ghost-divider: var(--btn-success-outline-border);
  }

  &--tone-ai {
    --btn-outline-bg: var(--btn-ai-outline-bg);
    --btn-outline-bg-hover: var(--btn-ai-outline-bg-hover);
    --btn-outline-border: var(--btn-ai-outline-border);
    --btn-outline-border-hover: var(--btn-ai-outline-border);
    --btn-outline-fg: var(--btn-ai-outline-fg);
    --btn-outline-divider: var(--btn-ai-divider);

    --btn-solid-bg: var(--btn-ai-solid-bg);
    --btn-solid-bg-hover: var(--btn-ai-solid-bg-hover);
    --btn-solid-fg: var(--btn-ai-solid-fg);
    --btn-solid-divider: var(--btn-ai-solid-divider);

    --btn-soft-bg: var(--btn-ai-soft-bg);
    --btn-soft-bg-hover: var(--btn-ai-soft-bg-hover);
    --btn-soft-fg: var(--btn-ai-soft-fg);
    --btn-soft-divider: var(--btn-ai-divider);

    --btn-ghost-fg: var(--btn-ai-ghost-fg);
    --btn-ghost-bg-hover: var(--btn-ai-ghost-bg-hover);
    --btn-ghost-divider: var(--btn-ai-divider);
  }

  &--tone-ai.ms-button--animated:hover:not(:disabled) :deep(svg) {
    animation: ms-button-ai-icon var(--btn-ai-icon-duration) both;
  }

  /* Underlined on hover only, so it does not shout at rest. */
  &--link {
    height: auto;
    width: auto;
    padding: 0;
    color: var(--btn-link-fg);
    text-underline-offset: var(--sp-4);

    &:hover:not(:disabled) {
      text-decoration: underline;
    }
  }

  &__spinner {
    width: 0.75em;
    height: 0.75em;
    border: 2px solid currentcolor;
    border-right-color: transparent;
    border-radius: var(--radius-pill);
    animation: ms-button-spin var(--duration-700) var(--ease-linear) infinite;
  }

  &__counter {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    min-width: var(--btn-counter-size);
    height: var(--btn-counter-size);
    padding: 0 var(--sp-4);
    border-radius: var(--radius-pill);
    font-size: var(--font-size-11);
    font-weight: var(--font-weight-semibold);
    font-variant-numeric: tabular-nums;
    line-height: 1;

    &--base {
      background-color: var(--btn-counter-base-bg);
    }

    &--accent {
      color: var(--btn-counter-accent-fg);
      background-color: var(--btn-counter-accent-bg);
    }

    &--danger {
      color: var(--btn-counter-danger-fg);
      background-color: var(--btn-counter-danger-bg);
    }

    &--success {
      color: var(--btn-counter-success-fg);
      background-color: var(--btn-counter-success-bg);
    }
  }
}

/* One turn while scaled up, then a spring back down to rest. */
@keyframes ms-button-ai-icon {
  0% {
    transform: rotate(0deg) scale(1);
    animation-timing-function: var(--ease-out);
  }

  25% {
    transform: rotate(80deg) scale(1.22);
    animation-timing-function: var(--ease-in-out);
  }

  70% {
    transform: rotate(300deg) scale(1.22);
    animation-timing-function: var(--ease-spring);
  }

  100% {
    transform: rotate(360deg) scale(1);
  }
}

@keyframes ms-button-spin {
  to {
    transform: rotate(360deg);
  }
}

.ms-button__stack {
  display: contents;
}

.ms-button--captioned {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  block-size: auto;
  row-gap: 0;
  padding-block: var(--btn-caption-padding-block);
  text-align: start;

  .ms-button__caption {
    grid-column: 1;
    grid-row: 1;
    overflow: hidden;
    color: var(--btn-caption-fg);
    font-size: var(--btn-caption-font-size);
    font-weight: var(--font-weight-regular);
    line-height: var(--btn-caption-line-height);
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .ms-button__stack {
    display: flex;
    grid-column: 1;
    grid-row: 2;
    align-items: center;
    gap: var(--btn-gap);
    min-inline-size: 0;
  }

  /* The box the label is actually cut off in — see the template. */
  .ms-button__line {
    min-inline-size: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .ms-button__trailing {
    display: inline-flex;
    grid-column: 2;
    grid-row: 1 / span 2;
    align-items: center;
  }
}
</style>
